import isNullOrUndefined from "_shared/_utilities/is-null-or-undefined";
import { InvalidEntityMemberJSON } from "_shared/entity-members/entity-member";
import FeeScheduleBillingCode from "_shared/fee-schedules/categories/billing-codes/fee-schedule-billing-code";
import FeeScheduleCategoryDescription from "_shared/fee-schedules/categories/descriptions/fee-schedule-category-description";
import FeeScheduleCategorySchema, { 
  FeeScheduleCategoryParams, 
  FeeScheduleCategoryJSON 
} from "_shared/fee-schedules/categories/fee-schedule-category-schema";
import Fee from "_shared/fee-schedules/categories/fees/fee";
import FeeScheduleCategoryName from "_shared/fee-schedules/categories/names/fee-schedule-category-name";
import Guid from "_shared/guids/guid";
import { formatCurrency } from "_shared/money/currencies/currency";
import { FeeScheduleCategoryMaskedCurrency, MaskedFeeSchedule } from "work/_shared/masked-fields";
import { IRedlineableField, JSONType } from "work/proposals/redlining/field-redlines/field-redline";

export default class FeeScheduleCategory implements IRedlineableField {
  public static get EXAMPLE(): FeeScheduleCategory {
    return new FeeScheduleCategory({
      id: Guid.generate(),
      name: new FeeScheduleCategoryName("Category Test"),
      description: new FeeScheduleCategoryDescription("Category Description Test"),
      currencyIsMasked: false,
      billingCode: new FeeScheduleBillingCode("TestCode"),
      fee: Fee.EXAMPLE
    });
  }

  public static create(
      params: FeeScheduleCategoryParams
    ): FeeScheduleCategory | FeeScheduleCategoryMaskedCurrency {
    const parsed = FeeScheduleCategorySchema.validate(params);
    if (isNullOrUndefined(parsed)) {
      throw new Error("Invalid FeeScheduleCategory parameters.");
    }
    if (parsed.currencyIsMasked) {
      return new FeeScheduleCategoryMaskedCurrency(parsed);
    }

    return new FeeScheduleCategory(parsed);
  }

  public static fromJSON(json: FeeScheduleCategoryJSON): FeeScheduleCategory | FeeScheduleCategoryMaskedCurrency {
    if (isNullOrUndefined(json)) return json;
    const params = FeeScheduleCategorySchema.deserialize(json);
    if (params.currencyIsMasked) return new FeeScheduleCategoryMaskedCurrency(params);
    return new FeeScheduleCategory(params);
  }

  public static fromUnknownJSON(json: JSONType): FeeScheduleCategory | FeeScheduleCategoryMaskedCurrency | null | undefined {
    if (isNullOrUndefined(json)) return json;
    else if (isValidFeeScheduleCategoryJSON(json, { throw: true })) return FeeScheduleCategory.fromJSON(json);
    throw new Error("Cannot create FeeScheduleCategory from invalid JSON");
  }

  public static fromJSONArray(
    jsonArray: FeeScheduleCategoryJSON[] | undefined
  ): (FeeScheduleCategory | FeeScheduleCategoryMaskedCurrency)[] | undefined {
    if (isNullOrUndefined(jsonArray)) return jsonArray;
    return jsonArray.map((json) => FeeScheduleCategory.fromJSON(json));
  }

  public readonly id: Guid;
  public readonly name: FeeScheduleCategoryName;
  public readonly description?: FeeScheduleCategoryDescription | null;
  public readonly fee?: Fee | null;
  public readonly billingCode?: FeeScheduleBillingCode | null;

  public constructor(params: FeeScheduleCategoryParams) {
    this.id = params.id;
    this.name = params.name;
    this.description = params.description;
    this.fee = params.fee;
    this.billingCode = params.billingCode;
  }

  public get isEmpty(): boolean {
    // A FeeScheduleCategory is never considered empty, required by IRedlineableField
    return false;
  }

  public get feeString(): string {
    if (!this.fee)
      return `(Deferred Fee)`;
    return `${this.fee?.unit ? 'Rate'
      : 'Fee'}: ${formatCurrency(this.fee?.rate)} ${this?.fee?.rate?.currency.toString() ?? ''} ${this.fee?.unit
        ? '/ ' + this.fee?.unit : ''}`;
  }

  public toJSON(): FeeScheduleCategoryJSON {
    return FeeScheduleCategorySchema.serialize(this);
  }

  public clone(): FeeScheduleCategory {
    return new FeeScheduleCategory({
      id: this.id.clone(),
      name: this.name.clone(),
      description: this.description ? this.description.clone() : this.description,
      fee: this.fee ? this.fee.clone() : this.fee,
      billingCode: this.billingCode ? this.billingCode.clone() : this.billingCode,
    });
  }

  public isEqualTo(other: FeeScheduleCategory | FeeScheduleCategoryMaskedCurrency | null | undefined): boolean {
    if (!other) return false;
    if (!(other instanceof FeeScheduleCategory)) return false;
    return (Boolean(
      ((!this.name && !other.name) || this.name?.isEqualTo(other.name)) &&
      ((!this.description && !other.description) || this.description?.isEqualTo(other.description)) &&
      ((!this.fee && !other.fee) || this.fee?.isEqualTo(other.fee ?? null)) &&
      ((!this.billingCode && !other.billingCode) || this.billingCode?.isEqualTo(other.billingCode))
    ));
  }

  public toString(): string {
    let feeString: string;
    if (this.fee) {
      feeString = `${this.name.value}: ${this.description?.toString()}\n
      ${this.fee?.unit ? 'Rate' : 'Fee'}: ${formatCurrency(this.fee?.rate)} ${this?.fee?.rate?.currency.toString() ??
        ''} ${this.fee?.unit ? '/ ' + this.fee.unit : ''}`;
    } else {
      feeString = (this.description ? `${this.name.value}: ${this.description.toString()}\n` : "") + '(Deferred Fee)';
    }
    if (this.billingCode) {
      feeString += `, Billing Code: ${this.billingCode.toString()}`;
    }
    return feeString;
  }
}

export type MaskableFeeSchedule = FeeScheduleCategory[] | FeeScheduleCategoryMaskedCurrency[] | MaskedFeeSchedule;

function isValidFeeScheduleCategoryJSON(json: JSONType, options?: { throw: boolean }): json is FeeScheduleCategoryJSON {
  options = options ?? { throw: false };
  if (isNullOrUndefined(json)) return true;

  if (
    typeof json !== "object" 
    || json === null 
    || FeeScheduleCategorySchema.FeeScheduleCategoryJSONZ.safeParse(json).success === false
  ) {
    if (options.throw) throw new InvalidEntityMemberJSON({ json });
    return false;
  }

  return true;
}
